import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    Divider,
    Snackbar,
    Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getFirestore, collection, getDocs, query, orderBy as firestoreOrderBy, doc, deleteDoc, updateDoc, runTransaction } from 'firebase/firestore';
import moment from 'moment';
import { utils, writeFile } from 'xlsx';

const firestore = getFirestore();

function ListTrabajos() {
    const [trabajos, setTrabajos] = useState([]);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [detailDialog, setDetailDialog] = useState({ open: false, trabajo: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, trabajo: null });
    const [reportDialog, setReportDialog] = useState({ open: false });
    const [reportFilters, setReportFilters] = useState({
        fechaDesde: moment().startOf('month'),
        fechaHasta: moment().endOf('month'),
        cuadrilla: '',
        zona: 'zona1'
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchTrabajos(),
                fetchCuadrillas()
            ]);
        } catch (error) {
            console.error('Error cargando datos:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTrabajos = async () => {
        const q = query(
            collection(firestore, 'trabajos_huawei'),
            firestoreOrderBy('timestamp', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const trabajosData = [];
        
        querySnapshot.forEach((docSnap) => {
            trabajosData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        
        setTrabajos(trabajosData);
    };

    const fetchCuadrillas = async () => {
        const q = query(collection(firestore, 'cuadrillas_huawei'), firestoreOrderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const cuadrillasData = [];
        querySnapshot.forEach((docSnap) => {
            cuadrillasData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setCuadrillas(cuadrillasData.filter(c => c.tipo !== 'deposito'));
    };

    const handleViewDetail = (trabajo) => {
        setDetailDialog({ open: true, trabajo });
    };

    const handleCloseDetail = () => {
        setDetailDialog({ open: false, trabajo: null });
    };

    const handleOpenReportDialog = () => {
        setReportDialog({ open: true });
    };

    const handleCloseReportDialog = () => {
        setReportDialog({ open: false });
    };

    const handleReportFilterChange = (field, value) => {
        setReportFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleEdit = (trabajo) => {
        window.location.href = `/huawei/trabajo-edit/${trabajo.docId}`;
    };

    const handleDelete = (trabajo) => {
        setDeleteDialog({ open: true, trabajo });
    };

    const handleCloseDelete = () => {
        setDeleteDialog({ open: false, trabajo: null });
    };

    const confirmDelete = async () => {
        try {
            const trabajoRef = doc(firestore, 'trabajos_huawei', deleteDialog.trabajo.docId);
            await deleteDoc(trabajoRef);
            showSnackbar('Trabajo eliminado exitosamente', 'success');
            setDeleteDialog({ open: false, trabajo: null });
            fetchTrabajos();
        } catch (error) {
            console.error('Error al eliminar trabajo:', error);
            showSnackbar('Error al eliminar el trabajo', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const getFilteredTrabajosForReport = () => {
        const desde = reportFilters.fechaDesde.startOf('day').valueOf();
        const hasta = reportFilters.fechaHasta.endOf('day').valueOf();
        
        return trabajos.filter(trabajo => {
            const fechaValida = trabajo.timestamp >= desde && trabajo.timestamp <= hasta;
            const cuadrillaValida = !reportFilters.cuadrilla || trabajo.cuadrilla.docId === reportFilters.cuadrilla;
            return fechaValida && cuadrillaValida;
        });
    };

    const generateReport = () => {
        const trabajosFiltrados = getFilteredTrabajosForReport();
        
        if (trabajosFiltrados.length === 0) {
            return null;
        }

        // Agrupar servicios y calcular totales
        const resumenServicios = {};
        let totalGeneral = 0;

        trabajosFiltrados.forEach(trabajo => {
            trabajo.servicios?.forEach(servicio => {
                const key = servicio.codigo;
                if (!resumenServicios[key]) {
                    resumenServicios[key] = {
                        codigo: servicio.codigo,
                        descripcion: servicio.descripcion,
                        unidad: servicio.unidad,
                        cantidad: 0,
                        precioUnitario: servicio[reportFilters.zona] || 0,
                        subtotal: 0
                    };
                }
                resumenServicios[key].cantidad += servicio.cantidad;
                resumenServicios[key].subtotal = resumenServicios[key].cantidad * resumenServicios[key].precioUnitario;
                totalGeneral += servicio.cantidad * (servicio[reportFilters.zona] || 0);
            });
        });

        return {
            trabajos: trabajosFiltrados,
            resumenServicios: Object.values(resumenServicios),
            totalGeneral
        };
    };

    const exportReportToExcel = () => {
        const reporte = generateReport();
        if (!reporte) return;

        // Hoja 1: Resumen de servicios
        const resumenData = reporte.resumenServicios.map(s => ({
            'Código': s.codigo,
            'Descripción': s.descripcion,
            'Unidad': s.unidad,
            'Cantidad Total': s.cantidad,
            'Precio Unitario': s.precioUnitario,
            'Subtotal': s.subtotal
        }));

        resumenData.push({
            'Código': '',
            'Descripción': 'TOTAL A PAGAR',
            'Unidad': '',
            'Cantidad Total': '',
            'Precio Unitario': '',
            'Subtotal': reporte.totalGeneral
        });

        // Hoja 2: Detalle de trabajos
        const detalleData = [];
        reporte.trabajos.forEach(trabajo => {
            trabajo.servicios?.forEach(servicio => {
                detalleData.push({
                    'Fecha': trabajo.fecha,
                    'Cuadrilla': trabajo.cuadrilla.numero,
                    'Obra': trabajo.obra?.nombre || 'N/A',
                    'Código Servicio': servicio.codigo,
                    'Descripción': servicio.descripcion,
                    'Cantidad': servicio.cantidad,
                    'Precio': servicio[reportFilters.zona] || 0,
                    'Subtotal': servicio.cantidad * (servicio[reportFilters.zona] || 0)
                });
            });
        });

        const wb = utils.book_new();
        
        const wsResumen = utils.json_to_sheet(resumenData);
        utils.book_append_sheet(wb, wsResumen, 'Resumen');
        
        const wsDetalle = utils.json_to_sheet(detalleData);
        utils.book_append_sheet(wb, wsDetalle, 'Detalle');

        const cuadrillaInfo = reportFilters.cuadrilla 
            ? cuadrillas.find(c => c.docId === reportFilters.cuadrilla)?.numero || 'Todas'
            : 'Todas';
        
        writeFile(wb, `Reporte_Trabajos_${cuadrillaInfo}_${reportFilters.fechaDesde.format('YYYYMMDD')}_${reportFilters.fechaHasta.format('YYYYMMDD')}.xlsx`);
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '-';
        return new Intl.NumberFormat('es-PY', {
            style: 'currency',
            currency: 'PYG',
            minimumFractionDigits: 0
        }).format(price);
    };

    const filteredTrabajos = trabajos.filter(trabajo => {
        const searchLower = searchTerm.toLowerCase();
        return (
            trabajo.fecha?.toLowerCase().includes(searchLower) ||
            trabajo.cuadrilla?.numero?.toLowerCase().includes(searchLower) ||
            trabajo.obra?.nombre?.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    const reporte = reportDialog.open ? generateReport() : null;

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Trabajos Ejecutados - Huawei
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AssessmentIcon />}
                            onClick={handleOpenReportDialog}
                            color="secondary"
                        >
                            Generar Reporte
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            href="/huawei/trabajo-add"
                        >
                            Nuevo Trabajo
                        </Button>
                    </Box>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Buscar por fecha, cuadrilla u obra"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar trabajos..."
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Chip label={`Total: ${filteredTrabajos.length} trabajos`} color="primary" />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Cuadrilla</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Obra</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Servicios</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Materiales</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTrabajos.map((trabajo) => (
                                <TableRow 
                                    key={trabajo.docId}
                                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                                >
                                    <TableCell>{trabajo.fecha}</TableCell>
                                    <TableCell>{trabajo.cuadrilla?.usuario ? `${trabajo.cuadrilla.usuario} - ${trabajo.cuadrilla.numero}` : trabajo.cuadrilla?.numero}</TableCell>
                                    <TableCell>{trabajo.obra?.nombre || 'N/A'}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Chip 
                                            label={trabajo.servicios?.length || 0}
                                            size="small"
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Chip 
                                            label={trabajo.materiales?.length || 0}
                                            size="small"
                                            color="default"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => handleViewDetail(trabajo)}
                                            title="Ver detalle"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton
                                            color="info"
                                            size="small"
                                            onClick={() => handleEdit(trabajo)}
                                            title="Editar"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDelete(trabajo)}
                                            title="Eliminar"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredTrabajos.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchTerm ? `No se encontraron trabajos para "${searchTerm}"` : 'No hay trabajos registrados. Crea un nuevo trabajo para comenzar.'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Dialog de Detalle */}
            <Dialog open={detailDialog.open} onClose={handleCloseDetail} maxWidth="md" fullWidth>
                <DialogTitle>
                    Detalle del Trabajo - {detailDialog.trabajo?.fecha}
                </DialogTitle>
                <DialogContent>
                    {detailDialog.trabajo && (
                        <Box>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Información General
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography><strong>Fecha:</strong> {detailDialog.trabajo.fecha}</Typography>
                                <Typography><strong>Cuadrilla:</strong> {detailDialog.trabajo.cuadrilla?.usuario ? `${detailDialog.trabajo.cuadrilla.usuario} - ${detailDialog.trabajo.cuadrilla.numero}` : detailDialog.trabajo.cuadrilla?.numero}</Typography>
                                <Typography><strong>Obra:</strong> {detailDialog.trabajo.obra?.nombre || 'N/A'}</Typography>
                                {detailDialog.trabajo.observaciones && (
                                    <Typography><strong>Observaciones:</strong> {detailDialog.trabajo.observaciones}</Typography>
                                )}
                            </Box>

                            {detailDialog.trabajo.servicios && detailDialog.trabajo.servicios.length > 0 && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                        Servicios Realizados
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Cantidad</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailDialog.trabajo.servicios.map((servicio, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{servicio.codigo}</TableCell>
                                                        <TableCell>{servicio.descripcion}</TableCell>
                                                        <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                            {servicio.cantidad}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={servicio.unidad} size="small" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {detailDialog.trabajo.materiales && detailDialog.trabajo.materiales.length > 0 && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                        Materiales Utilizados
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Cantidad</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailDialog.trabajo.materiales.map((material, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{material.codigo}</TableCell>
                                                        <TableCell>{material.descripcion}</TableCell>
                                                        <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                            {material.cantidad}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={material.unidad} size="small" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Reporte */}
            <Dialog open={reportDialog.open} onClose={handleCloseReportDialog} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Generar Reporte de Trabajos y Pagos
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={3}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Fecha Desde"
                                        value={reportFilters.fechaDesde}
                                        onChange={(newValue) => handleReportFilterChange('fechaDesde', newValue)}
                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                        format="DD/MM/YYYY"
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Fecha Hasta"
                                        value={reportFilters.fechaHasta}
                                        onChange={(newValue) => handleReportFilterChange('fechaHasta', newValue)}
                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                        format="DD/MM/YYYY"
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Cuadrilla"
                                    value={reportFilters.cuadrilla}
                                    onChange={(e) => handleReportFilterChange('cuadrilla', e.target.value)}
                                >
                                    <MenuItem value="">Todas las cuadrillas</MenuItem>
                                    {cuadrillas.map((cuadrilla) => (
                                        <MenuItem key={cuadrilla.docId} value={cuadrilla.docId}>
                                            {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Zona de Precio"
                                    value={reportFilters.zona}
                                    onChange={(e) => handleReportFilterChange('zona', e.target.value)}
                                >
                                    <MenuItem value="zona1">Zona 1</MenuItem>
                                    <MenuItem value="zona2">Zona 2</MenuItem>
                                    <MenuItem value="zona3">Zona 3</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>

                        {reporte && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" gutterBottom>
                                    Resumen de Servicios
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Trabajos encontrados: {reporte.trabajos.length}
                                </Typography>
                                
                                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Cantidad Total</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Precio Unit.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reporte.resumenServicios.map((servicio, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{servicio.codigo}</TableCell>
                                                    <TableCell>{servicio.descripcion}</TableCell>
                                                    <TableCell><Chip label={servicio.unidad} size="small" /></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                        {servicio.cantidad}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right' }}>
                                                        {formatPrice(servicio.precioUnitario)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                        {formatPrice(servicio.subtotal)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                <TableCell colSpan={5} sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                    TOTAL A PAGAR
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.2rem', color: 'primary.main' }}>
                                                    {formatPrice(reporte.totalGeneral)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseReportDialog}>
                        Cerrar
                    </Button>
                    {reporte && reporte.trabajos.length > 0 && (
                        <Button 
                            onClick={exportReportToExcel}
                            variant="contained"
                            color="primary"
                            startIcon={<FileDownloadIcon />}
                        >
                            Descargar Excel
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmación de Eliminación */}
            <Dialog open={deleteDialog.open} onClose={handleCloseDelete}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar este trabajo del {deleteDialog.trabajo?.fecha}?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta acción no se puede deshacer. Se eliminará el registro del trabajo pero NO se afectará el stock.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListTrabajos;
